import { WorkProductTableStatus } from '@/components/work-product-table/WorkProductTableStatus'
import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type CellContext,
  type OnChangeFn,
  type SortingState,
} from '@tanstack/react-table'
import { useMemo } from 'react'

import { DocumentCheck, DraftSparkle } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import { Tag } from '@/components/tag/Tag'
import { SearchableText } from '@/components/searchable-text/SearchableText'
import { WorkProductTable, WorkProductTableUpdatedAt } from '@/components/work-product-table/WorkProductTable'
import { DraftRowMenu } from '@/drafting/destination/components/DraftRowMenu'
import {
  describeDraftsSorting,
  draftsCreatorSortingFn,
  draftsGlobalFilter,
  draftsNameSortingFn,
  draftsUpdatedAtSortingFn,
  kDraftsTableColumn,
} from '@/drafting/destination/utils/drafts-list-view'
import type { MatterArtifactDraftListRow } from '@/drafting/hooks/useMatterArtifactDrafts'

const columnHelper = createColumnHelper<MatterArtifactDraftListRow>()

interface DraftsTableProps {
  /** Every loaded draft. The table filters and orders these itself. */
  rows: MatterArtifactDraftListRow[]
  /** Filters the rows, bolds the matches, and names the query in the no-results message. */
  searchQuery: string
  /** Resets the query and returns focus to the search field. */
  onClearSearch: () => void
  sorting: SortingState
  onSortingChange: OnChangeFn<SortingState>
  isLoadingWithoutCachedDrafts: boolean
  isError: boolean
  onRetry: () => void
  /** Opens a draft in its source chat. Called on row click, Enter, and Space. */
  onOpenDraft: (row: MatterArtifactDraftListRow) => void
  /** Downloads a draft as DOCX from its row menu. */
  onDownloadDraft: (row: MatterArtifactDraftListRow) => void
  /**
   * Saves a copy of a draft into the matter's files from its row menu. Absent where the matter
   * cannot receive one, and offered only on the user's own drafts: the draft view gives a
   * collaborator the plain download alone, and this list follows the same rule.
   */
  onSaveDraftToMatterFiles?: (row: MatterArtifactDraftListRow) => void
  /** Matter artifact ids whose export is in flight, so those rows show their progress. */
  exportingMatterArtifactIds: readonly string[]
  /** Starts a new chat from the empty state. */
  onEmptyAction: () => void
}

/**
 * DraftsTable
 *
 * The Drafts destination list: one row per draft, with its name, last-modified time, and
 * creator. Every data column sorts from its header, matches of the active search are bolded, and
 * activating a row opens its draft.
 */
export function DraftsTable({
  rows,
  searchQuery,
  onClearSearch,
  sorting,
  onSortingChange,
  isLoadingWithoutCachedDrafts,
  isError,
  onRetry,
  onOpenDraft,
  onDownloadDraft,
  onSaveDraftToMatterFiles,
  exportingMatterArtifactIds,
  onEmptyAction,
}: DraftsTableProps) {
  const columns = useMemo(
    () => [
      columnHelper.accessor(kDraftsTableColumn.NAME, {
        header: 'Draft Name',
        sortingFn: draftsNameSortingFn,
        meta: {
          // Header 4px short of the cell because the header button adds its own 4px, so the label
          // and the row content both land on the 20px inset the design gives them.
          headerClassName: 'pl-4 pr-3 text-left font-medium',
          cellClassName: 'py-3 pl-5 pr-4 [@container_(min-width:_640px)]:pr-8',
          sortLabels: { ascendingLabel: 'Sort A-Z', descendingLabel: 'Sort Z-A' },
        },
        cell: ({ row, table }) => {
          const query = (table.getState().globalFilter as string) || ''

          return (
            <div className="flex min-w-0 items-center gap-3">
              <DraftSparkle className="hidden size-[18px] shrink-0 text-brand-neutral-600 [@container_(min-width:_640px)]:block" aria-hidden="true" />
              <span className="truncate text-brand-neutral-950" title={row.original.name}>
                <SearchableText text={row.original.name} searchQuery={query} />
              </span>
            </div>
          )
        },
      }),
      columnHelper.accessor(kDraftsTableColumn.UPDATED_AT, {
        header: 'Last Modified',
        sortingFn: draftsUpdatedAtSortingFn,
        meta: {
          // Hidden in CSS rather than dropped from the table, so the column keeps its place in
          // the sort state and comes back with the width.
          headerClassName: 'hidden pr-3 text-left font-medium [@container_(min-width:_640px)]:table-cell',
          cellClassName: 'hidden py-3 pl-1 pr-8 text-brand-neutral-800 [@container_(min-width:_640px)]:table-cell',
          sortLabels: { ascendingLabel: 'Sort old to new', descendingLabel: 'Sort new to old' },
          defaultSortDesc: true,
        },
        cell: ({ getValue }) => <WorkProductTableUpdatedAt updatedAt={getValue()} />,
      }),
      columnHelper.accessor((row) => row.creatorLabel ?? undefined, {
        id: kDraftsTableColumn.CREATOR,
        header: 'Created By',
        sortingFn: draftsCreatorSortingFn,
        // Creators that haven't resolved yet sort to the bottom in both directions, rather than an
        // empty label heading the list.
        sortUndefined: 'last',
        meta: {
          headerClassName: 'pr-3 text-left font-medium',
          cellClassName: 'py-3 pl-1 pr-2',
          sortLabels: { ascendingLabel: 'Sort A-Z', descendingLabel: 'Sort Z-A' },
        },
        cell: ({ row, getValue, table }) => {
          const query = (table.getState().globalFilter as string) || ''
          const creatorLabel = getValue()

          return (
            <div className="flex min-w-0 items-center gap-2.5">
              <span className="truncate text-brand-neutral-950" title={creatorLabel}>
                {creatorLabel !== undefined && <SearchableText text={creatorLabel} searchQuery={query} />}
              </span>
              {/* Held back with the name, so an unresolved creator's cell stays blank rather than tagged. */}
              {creatorLabel !== undefined && !row.original.isCreatedByCurrentUser && (
                <Tag variant="tonal" color="Black" size="2xs" textCase="normal">
                  VIEW ONLY
                </Tag>
              )}
            </div>
          )
        },
      }),
      columnHelper.display({
        id: kDraftsTableColumn.ACTIONS,
        // Named for a screen reader only: the design leaves the column's header blank.
        header: () => <span className="sr-only">Actions</span>,
        meta: { headerClassName: 'pr-3', cellClassName: 'py-3 pr-3' },
        cell: DraftActionsCell,
      }),
    ],
    []
  )

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    globalFilterFn: draftsGlobalFilter,
    // Table metadata rather than a closure in the column definitions: rebuilding those when an
    // export starts or ends gives every cell a new component type, remounting each row's menu.
    meta: { exportingMatterArtifactIds, onDownloadDraft, onSaveDraftToMatterFiles },
    state: { globalFilter: searchQuery, sorting },
    onSortingChange,
    // The artifact, not its version: one row per artifact, and a poll picking up a collaborator's
    // edit gives the row a new version id. Keying on that would remount the row, closing an open menu
    // and dropping keyboard focus.
    getRowId: (row) => row.matterArtifactId,
  })

  const visibleRows = table.getRowModel().rows
  const showStatusView = !isLoadingWithoutCachedDrafts && visibleRows.length === 0
  // A search that matched nothing is only a no-results state when there was something to match: an
  // empty matter keeps its own empty state whatever is typed.
  const noResultsQuery = showStatusView && rows.length > 0 && searchQuery.trim().length > 0 ? searchQuery : null

  return (
    <WorkProductTable
      table={table}
      onSortingChange={onSortingChange}
      onOpenRow={onOpenDraft}
      isLoading={isLoadingWithoutCachedDrafts}
      caption="Drafts in this matter"
      statusLabel={
        isLoadingWithoutCachedDrafts
          ? 'Loading drafts'
          : noResultsQuery !== null
          ? 'No drafts match your search'
          : visibleRows.length > 0
          ? describeDraftsSorting(sorting)
          : ''
      }
      statusView={
        <DraftsTableStatusView
          isError={isError && rows.length === 0}
          onRetry={onRetry}
          noResultsQuery={noResultsQuery}
          onClearSearch={onClearSearch}
          onEmptyAction={onEmptyAction}
        />
      }
    />
  )
}

/**
 * DraftActionsCell
 *
 * Declared here rather than inline in the column definition, so its identity is stable across
 * renders.
 */
function DraftActionsCell({ row, table }: CellContext<MatterArtifactDraftListRow, unknown>) {
  const { exportingMatterArtifactIds = [], onDownloadDraft, onSaveDraftToMatterFiles } = table.options.meta ?? {}

  return (
    // The row is the control, so the menu's own events stop here rather than reaching it.
    <div className="flex justify-end" onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
      <DraftRowMenu
        isExporting={exportingMatterArtifactIds.includes(row.original.matterArtifactId)}
        onDownload={() => onDownloadDraft?.(row.original)}
        onSaveToMatterFiles={onSaveDraftToMatterFiles && row.original.isCreatedByCurrentUser ? () => onSaveDraftToMatterFiles(row.original) : undefined}
      />
    </div>
  )
}

/**
 * DraftsTableStatusView
 *
 * Replaces the table's rows when there are none to show. Reached whenever no rows are *visible*,
 * which includes a list that loaded fine and was filtered down to nothing — so the caller gates
 * `isError` on there being no source rows at all.
 */
function DraftsTableStatusView({
  isError,
  onRetry,
  noResultsQuery,
  onClearSearch,
  onEmptyAction,
}: {
  isError: boolean
  onRetry: () => void
  /** The query that matched nothing, or null when this isn't a no-results state. */
  noResultsQuery: string | null
  onClearSearch: () => void
  onEmptyAction: () => void
}) {
  return (
    <WorkProductTableStatus isError={isError} onRetry={onRetry} noResultsQuery={noResultsQuery} onClearSearch={onClearSearch} resourceName="drafts">
      <div className="flex flex-col items-center justify-center bg-brand-neutral-50 p-8">
        <div className="flex w-full flex-col items-center gap-4 rounded-lg border border-brand-neutral-200 bg-white px-4 py-6">
          {/* The outlined document with the gold shield-check, from the design's icon/illustration —
            the same 62.5x40 illustration family as FolderCheck on the Matters blank state. */}
          <DocumentCheck className="h-10 w-[62.5px]" aria-hidden="true" />

          <div className="flex max-w-[420px] flex-col items-center gap-0.5 text-center">
            {/* The level below the page's `Drafts` h1. */}
            <h2 className="text-base font-medium leading-6 text-brand-neutral-950">Create your first draft</h2>
            <p className="text-sm leading-5 tracking-[-0.15px] text-brand-neutral-600">
              Drafts pull from the files and research in this matter. Start a chat and tell Paxton what to draft.
            </p>
          </div>

          <Button onClick={onEmptyAction} size="sm" variant="filled" color="primary">
            New Chat
          </Button>
        </div>
      </div>
    </WorkProductTableStatus>
  )
}
